/**
 * TanStack column ids for the request-overview and order-tracking tables, read directly off
 * `RequestOverviewTable.tsx` and `OrderTrackingTable.tsx` (each `accessorKey` or explicit `id`), and shared
 * with the matching `FilterFieldGroup.columnId` values in `request-view-state.ts` / `order-view-state.ts`.
 * Extracted so the table definition and the view-state can never drift apart silently: before this module
 * existed, both sides carried the same ids as separately hand-typed string literals, so renaming an
 * `accessorKey` left the filter group pointing at a column that no longer exists, with nothing catching it.
 * A Vitest test cannot import the `.tsx` table components at all (no rendered-component tier, see
 * @.claude/rules/common-testing.md), so this plain `.ts` module is also what the tests compare against.
 *
 * Pure string constants, no React, no network. Only the ids that participate in a `FilterFieldGroup` are
 * listed here; a table column outside every filter group has no entry, it never needs to be looked up
 * from either side.
 */

/** Every request-overview column id a `FilterFieldGroup` in `request-view-state.ts` points at. */
export const REQUEST_OVERVIEW_COLUMN_IDS = {
    info: "info",
    customerRequestNumber: "customerRequestNumber",
    requesterCompany: "requesterCompany",
    status: "status",
    validUntil: "validUntil",
    distanceMeters: "distanceMeters",
    deliveryDateRange: "deliveryDateRange",
    cartonGrade: "cartonGrade",
    fefcoCode: "fefcoCode",
    internalDimensions: "internalDimensions",
    quantityOptions1: "quantityOptions1",
    quantityOptions2: "quantityOptions2",
    quantityOptions3: "quantityOptions3"
} as const;

/** Every order-tracking column id a `FilterFieldGroup` in `order-view-state.ts` points at. */
export const ORDER_TRACKING_COLUMN_IDS = {
    info: "info",
    status: "status",
    plannedDeliveryDates: "plannedDeliveryDates",
    deliveryDate: "deliveryDate",
    orderNumber: "orderNumber",
    quoteNumber: "quoteNumber",
    deliveryAddress: "deliveryAddress"
} as const;
